/**
 * InfinitSales - Delegação de workspace (Etapa 2.2)
 *
 * Estado da sessão delegada (entrar no workspace de outra pessoa). Mantemos
 * isto em localStorage para sobreviver a reloads e guardamos o token REAL do
 * usuário para conseguir voltar ao próprio workspace ao sair.
 *
 * Mantido puro (só localStorage, sem importar o api) para evitar dependência
 * circular — o interceptor do api.js precisa importar este módulo.
 */
const KEY = 'infinitsales-delegation';

export function getDelegation() {
  try {
    return JSON.parse(localStorage.getItem(KEY));
  } catch {
    return null;
  }
}

export function isDelegated() {
  return !!getDelegation();
}

/**
 * Entra no workspace alvo: guarda o token real e ativa o token delegado.
 *
 * Delegação encadeada (trocar de workspace sem sair do atual): o token ativo
 * neste momento é um token DELEGADO — o token real do usuário é o que já está
 * guardado no marcador. Reutilizamos esse `realToken` raiz; capturar o token
 * ativo aqui sobrescreveria o marcador com um token delegado e o usuário
 * perderia o caminho de volta para a própria conta.
 *
 * Voltar para o próprio workspace (target === actor) não é delegação: ativa o
 * token e limpa o marcador.
 *
 * Lança Error('delegation_token_missing') se não houver token utilizável para
 * voltar — melhor falhar o clique do que gravar uma sessão sem rota de saída.
 *
 * NÃO removemos o cache `user` do localStorage: o AuthContext só inicializa a
 * sessão quando existem `user` E `token` juntos. No reload, o init chama
 * getProfile() com o token ativo (delegado) e sobrescreve a identidade pela do
 * alvo. Remover `user` aqui fazia o init pular a sessão e cair no /login.
 * @param {{ token: string, target: {user_id, name}, actor: {user_id, name} }} payload
 */
export function startDelegation({ token, target, actor }) {
  if (!token) {
    throw new Error('delegation_token_missing');
  }

  // Voltar ao próprio workspace: sessão normal, sem marcador.
  if (target?.user_id && actor?.user_id && target.user_id === actor.user_id) {
    localStorage.setItem('token', token);
    localStorage.removeItem(KEY);
    return;
  }

  const existing = getDelegation();
  const realToken = existing?.realToken || localStorage.getItem('token');
  if (!realToken) {
    throw new Error('delegation_token_missing');
  }

  localStorage.setItem(
    KEY,
    JSON.stringify({
      realToken,
      // Guardamos o token delegado para o stale-check comparar tokens (fato
      // síncrono) em vez de identidades carregadas async pelo AuthContext.
      delegatedToken: token,
      targetUserId: target?.user_id,
      targetName: target?.name,
      actorUserId: actor?.user_id,
      actorName: actor?.name,
    }),
  );
  localStorage.setItem('token', token);
}

/**
 * Sai da sessão delegada e restaura o token real do usuário. O getProfile() do
 * AuthContext, no reload seguinte, troca a identidade de volta para o ator.
 */
export function endDelegation() {
  const d = getDelegation();
  if (d?.realToken) {
    localStorage.setItem('token', d.realToken);
  }
  localStorage.removeItem(KEY);
}

/**
 * Remove apenas o marcador de delegação SEM mexer no token ativo. Usado para
 * auto-limpar um banner obsoleto quando a sessão atual já não é a do alvo
 * (ex.: o usuário deslogou/relogou com um marcador antigo ainda presente).
 */
export function clearDelegationMarker() {
  localStorage.removeItem(KEY);
}
